import type { PrintSettings } from "@nevisign/reader-core";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { appendLog } from "./logger";

type NativePrintPlugin = {
  printHtml(options: {
    html: string;
    jobName?: string;
  }): Promise<{ ok: boolean }>;
};

const NativePrint = registerPlugin<NativePrintPlugin>("NativePrint");

function pageCssSize(settings: PrintSettings): string {
  if (settings.pageSize === "Letter") {
    return settings.orientation === "landscape" ? "11in 8.5in" : "8.5in 11in";
  }
  return settings.orientation === "landscape" ? "297mm 210mm" : "210mm 297mm";
}

/**
 * Build print stylesheet from user settings.
 */
export function buildPrintStyle(settings: PrintSettings): string {
  const size = pageCssSize(settings);
  const margin = `${settings.marginMm}mm`;
  const gray =
    settings.colorMode === "grayscale"
      ? `html, body, img, canvas { filter: grayscale(1) !important; }`
      : "";
  const comments = settings.includeComments
    ? ""
    : `.nr-comment-range, .nr-comment-anchor, .comment-marker { display: none !important; }`;

  return `
@media print {
  @page {
    size: ${size};
    margin: ${margin};
  }
  html, body {
    background: white !important;
    color: black !important;
    -webkit-print-color-adjust: ${settings.colorMode === "color" ? "exact" : "economy"};
    print-color-adjust: ${settings.colorMode === "color" ? "exact" : "economy"};
  }
  .chrome, .chrome-menu, .reader-toolbar, .floating-reader-bar,
  .selection-bottom-bar, .assistant-fab, .assistant-sheet,
  .side-panel, .status-banner, .empty-state,
  .print-sheet, .update-banner, .comment-focus-sheet, .add-comment-sheet,
  .menu-btn, button.icon-btn, .nr-no-print {
    display: none !important;
  }
  .app, .workspace, .reader-pane, .canvas-scroll {
    height: auto !important;
    overflow: visible !important;
    position: static !important;
    display: block !important;
    padding: 0 !important;
    margin: 0 !important;
    background: white !important;
  }
  .pdf-text-layer { opacity: 0.01 !important; }
  ${gray}
  ${comments}
}
`;
}

function ensurePrintStyle(settings: PrintSettings): HTMLStyleElement {
  const id = "nr-print-style";
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement("style");
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = buildPrintStyle(settings);
  return el;
}

/**
 * Open system print dialog (Android PrintManager via WebView / native plugin).
 */
export async function printOpenDocument(opts: {
  settings: PrintSettings;
  jobName: string;
  rootSelector?: string;
}): Promise<void> {
  ensurePrintStyle(opts.settings);
  appendLog("info", "print", `printing ${opts.jobName}`, opts.settings);

  const root =
    document.querySelector(opts.rootSelector ?? ".canvas-scroll") ??
    document.body;

  if (Capacitor.isNativePlatform()) {
    try {
      const clone = root.cloneNode(true) as HTMLElement;
      const html = `<!DOCTYPE html><html lang="fa" dir="rtl"><head>
<meta charset="utf-8"/>
<title>${opts.jobName}</title>
<style>
  body { font-family: Tahoma, Vazirmatn, sans-serif; direction: rtl; color: #000; }
  img, canvas { max-width: 100%; }
  ${buildPrintStyle(opts.settings).replace("@media print {", "").replace(/}\s*$/, "")}
</style>
</head><body>${clone.innerHTML}</body></html>`;
      await NativePrint.printHtml({ html, jobName: opts.jobName });
      return;
    } catch (err) {
      appendLog("warn", "print", "native print failed, falling back", err);
    }
  }

  await new Promise<void>((resolve) => {
    const done = () => {
      window.removeEventListener("afterprint", done);
      resolve();
    };
    window.addEventListener("afterprint", done);
    window.print();
    // Fallback if afterprint never fires
    window.setTimeout(done, 1500);
  });
}
